import { CircleAlert, CirclePlus, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatVNDShort } from '@/lib/format';
import type { ImportSummary, SalesImportRow, SalesImportRowResult } from '@/lib/api';

export interface SalesImportPreviewProps {
  rows: SalesImportRow[];
  results: SalesImportRowResult[];
  summary: ImportSummary;
}

function StatusBadge({ status }: { status: 'new' | 'update' | 'error' }) {
  if (status === 'new') {
    return (
      <Badge variant="secondary" className="h-5 gap-0.5 border-emerald-500/30 bg-emerald-500/10 text-emerald-700">
        <CirclePlus className="h-2.5 w-2.5" />
        New
      </Badge>
    );
  }
  if (status === 'update') {
    return (
      <Badge variant="secondary" className="h-5 gap-0.5 border-amber-500/30 bg-amber-500/10 text-amber-700">
        <RefreshCw className="h-2.5 w-2.5" />
        Update
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="h-5 gap-0.5 border-destructive/30 bg-destructive/10 text-destructive">
      <CircleAlert className="h-2.5 w-2.5" />
      Error
    </Badge>
  );
}

function formatMonth(month: string): string {
  const m = month.match(/^(\d{4})-(\d{2})$/);
  if (!m) return month;
  return `T${parseInt(m[2]!, 10)}/${m[1]}`;
}

export function SalesImportPreview({ rows, results, summary }: SalesImportPreviewProps) {
  const byIdx = new Map(results.map((r) => [r.index, r]));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 p-3 text-sm">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Tổng:</span>
          <span className="font-semibold tabular-nums">{summary.total}</span>
        </div>
        <div className="h-4 w-px bg-border" />
        <div className="flex items-center gap-1.5">
          <CirclePlus className="h-3.5 w-3.5 text-emerald-600" />
          <span className="text-muted-foreground">Mới:</span>
          <span className="font-semibold tabular-nums text-emerald-700">{summary.new}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <RefreshCw className="h-3.5 w-3.5 text-amber-600" />
          <span className="text-muted-foreground">Update:</span>
          <span className="font-semibold tabular-nums text-amber-700">{summary.update}</span>
        </div>
        {summary.errors > 0 && (
          <div className="flex items-center gap-1.5">
            <CircleAlert className="h-3.5 w-3.5 text-destructive" />
            <span className="text-muted-foreground">Lỗi:</span>
            <span className="font-semibold tabular-nums text-destructive">{summary.errors}</span>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-16">Trạng thái</TableHead>
              <TableHead className="w-44">SKU</TableHead>
              <TableHead className="w-24">Tháng</TableHead>
              <TableHead className="w-20 text-right">Units</TableHead>
              <TableHead className="w-28 text-right">Doanh thu</TableHead>
              <TableHead className="w-28 text-right">Lợi nhuận</TableHead>
              <TableHead className="w-20 text-right">Tồn kho</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => {
              const result = byIdx.get(i);
              const status = result?.status ?? 'error';
              return (
                <TableRow
                  key={`${row.monitor_id}-${row.month}-${i}`}
                  className={cn(
                    status === 'new' && 'bg-emerald-50/40',
                    status === 'update' && 'bg-amber-50/40',
                    status === 'error' && 'bg-destructive/5'
                  )}
                >
                  <TableCell>
                    <StatusBadge status={status} />
                  </TableCell>
                  <TableCell className="text-xs uppercase tracking-wider text-muted-foreground">
                    {row.monitor_id}
                    {result?.error && (
                      <div className="mt-1 text-[11px] normal-case tracking-normal text-destructive">
                        {result.error}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">{formatMonth(row.month)}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {row.units_sold.toLocaleString('vi-VN')}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-primary">
                    {formatVNDShort(row.revenue_vnd)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-right tabular-nums text-sm',
                      row.margin_vnd < 0 && 'text-destructive'
                    )}
                  >
                    {formatVNDShort(row.margin_vnd)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {row.stock_qty.toLocaleString('vi-VN')}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
